import * as React from 'react';
import { Button, Icon } from '@teamsnap/teamsnap-ui';
import { useFieldArray } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import { FieldType } from '../types';
import { orderFieldsByTarget } from '../Questions';
import ConditionalLogic, {
  ComparisonType,
  ConditionType,
  VisibilityRule,
} from './ConditionalLogic';

import * as GetFormFields from 'core/gql/queries/show-form-fields';
import * as GqlOrganizationFormFieldDefinitions from 'core/gql/queries/get-organization-form-field-definitions';

import './AdditionalQuestionConditionalLogic.scss';

type Props = {
  orgId: number;
  formId: number;
  question: FieldType;
  visibilityRules: any[];
  formHook: any;
};

const AdditionalQuestionConditionalLogic = ({
  orgId,
  formId,
  question,
  visibilityRules,
  formHook,
}: Props) => {
  const [hasLogic, setHasLogic] = React.useState(false);
  const [globalComparison, setGlobalComparison] =
    React.useState<ComparisonType>(ComparisonType.AND);

  const { control } = formHook;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'logic',
    keyName: 'key',
  });

  const formFieldsQuery =
    useQuery(GetFormFields.QUERY, {
      variables: { formId: formId, organizationId: orgId },
      skip: question.locked || !formId,
    })?.data?.showRegistrationForm?.fields || null;

  const organizationFieldDefinitionList =
    useQuery(GqlOrganizationFormFieldDefinitions.QUERY, {
      variables: { organization_id: orgId },
    })?.data?.organizationFormFieldDefinitions || [];

  let fieldTypeQuestionOptions = !question.locked
    ? orderFieldsByTarget(formFieldsQuery).map((f) => {
        const definition = organizationFieldDefinitionList.find(
          (d) => +d.id === +f.form_field_definition_id
        );
        if (!(definition.external_id || definition.external_type)) {
          return {
            ...f,
            organization_id: definition.organization_id,
            name: definition.name,
          };
        } else {
          return null
        }
      })
      .filter(Boolean)
    : [];

  const participationGroupQuestion = fieldTypeQuestionOptions.find(
    (f) => f.internal && f.name === 'participation_group'
  );
  fieldTypeQuestionOptions = fieldTypeQuestionOptions.filter(
    (f) =>
      f.type === 'select' &&
      f.enabled &&
      !f.internal
  );
  fieldTypeQuestionOptions.unshift(participationGroupQuestion);

  const showConditionalLogicSection = (show) => {
    setHasLogic(show);
  };

  const addLogic = () => {
    append({
      id: '',
      comparison: globalComparison,
      question: '',
      condition: '',
      answer: '',
    });
  };

  React.useEffect(() => {
    if (!hasLogic && fields.length > 0) {
      remove();
    } else if (hasLogic && visibilityRules.length > 0) {
      const rules = [];
      setGlobalComparison(visibilityRules[0].chainingComparison);
      visibilityRules.forEach(
        ({
          id,
          answerToMatch,
          targetFormFieldId,
          comparison,
          chainingComparison,
        }) => {
          rules.push({
            id: `${id}`,
            comparison: chainingComparison as ComparisonType,
            question: `${targetFormFieldId}`,
            condition: comparison as ConditionType,
            answer: answerToMatch,
          });
        }
      );
      append(rules);
    } else if (hasLogic && fields.length === 0) {
      append({
        id: '',
        comparison: globalComparison,
        question: '',
        condition: '',
        answer: '',
      });
    }
  }, [hasLogic]);

  React.useEffect(() => {
    if (visibilityRules.length > 0) {
      setHasLogic(true);
    }
  }, [visibilityRules]);

  return (
    <div
      data-testid="conditional-logic__container"
      className="conditional-logic__container"
    >
      <Button
        type="button"
        mods={`u-borderNone u-borderRadiusNone ${
          hasLogic ? 'u-colorNeutral7' : 'u-colorSuccess6 u-bgNeutral2'
        }`}
        onClick={() => showConditionalLogicSection(false)}
        isDisabled={question?.locked ?? false}
        testId="conditional-logic-always-show"
      >
        {!hasLogic && <Icon name="check" mods="u-fontSizeXs u-spaceRightXs" />}
        Always Show
      </Button>
      {!question.locked && (
        <Button
          type="button"
          mods={`u-borderNone u-borderRadiusNone ${
            hasLogic ? 'u-colorSuccess6 u-bgNeutral2' : 'u-colorNeutral7'
          }`}
          onClick={() => showConditionalLogicSection(true)}
          isDisabled={question?.locked ?? false}
          testId="conditional-logic-only-show"
        >
          {hasLogic && <Icon name="check" mods="u-fontSizeXs u-spaceRightXs" />}
          Only Show When
        </Button>
      )}
      {hasLogic && (
        <div className="u-padSidesSm u-padBottomSm u-padTopXs u-bgNeutral2">
          <small>
            Only questions of type "select" are available at this time.
          </small>
          {fields.map((f, i) => (
            <React.Fragment key={`${f.key}`}>
              <ConditionalLogic
                index={i}
                question={question}
                field={f as VisibilityRule}
                fields={fields as VisibilityRule[]}
                comparison={globalComparison}
                options={fieldTypeQuestionOptions.filter(
                  (i) => i !== undefined
                )}
                formHook={formHook}
                onRemove={() => remove(i)}
                setComparison={setGlobalComparison}
              />
            </React.Fragment>
          ))}
          <Button
            testId="conditional-logic-add-condition"
            type="link"
            icon="plus"
            mods="u-padSidesXs btn-add-condition"
            onClick={addLogic}
          >
            Add Condition
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdditionalQuestionConditionalLogic;
